import { ReactNode } from 'react';
import { Box, Chip, Divider, Typography, useTheme } from '@mui/material';

type UsageMeterCardProps = {
  title: string;
  icon: ReactNode;
  label: string;
  used?: number;
  limit?: number;
  unit?: string;
  badge?: string;
  format?: (value: number) => string;
};

const defaultFormat = (value: number) => Math.round(value).toLocaleString();

export default function UsageMeterCard({
  title,
  icon,
  label,
  used = 0,
  limit = 0,
  unit = '',
  badge,
  format = defaultFormat
}: UsageMeterCardProps) {
  const theme = useTheme();
  const safeUsed = Math.max(Number(used) || 0, 0);
  const safeLimit = Math.max(Number(limit) || 0, 0);
  const hasLimit = safeLimit > 0;
  const isOver = hasLimit && safeUsed > safeLimit;
  const rawPercentage = hasLimit ? (safeUsed / safeLimit) * 100 : 0;
  const percentage = Math.min(Math.round(rawPercentage), 100);
  const isWarning = hasLimit && !isOver && rawPercentage >= 70;

  const mutedColor = theme.palette.text.secondary;
  const statusColor = isOver ? theme.palette.error.main : isWarning ? theme.palette.warning.main : mutedColor;
  const primaryBarColor = isWarning ? theme.palette.warning.main : theme.palette.primary.main;
  const primaryWidth = isOver ? (safeLimit / safeUsed) * 100 : Math.min(rawPercentage, 100);
  const overWidth = isOver ? 100 - primaryWidth : 0;

  const withUnit = (value: number) => [format(value), unit].filter(Boolean).join(' ');

  const statusText = !hasLimit
    ? null
    : isOver
      ? `${format(safeUsed - safeLimit)} over`
      : `${format(safeLimit - safeUsed)} remaining`;

  return (
    <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius='10px' height='100%'>
      <Box display='flex' alignItems='center' justifyContent='space-between' gap={1} px={2} py={1.5}>
        <Box display='flex' alignItems='center' gap={1.25} minWidth={0}>
          <Box
            bgcolor={theme.palette.primary.light}
            color={theme.palette.primary.dark}
            borderRadius='50%'
            height={32}
            width={32}
            flexShrink={0}
            display='flex'
            alignItems='center'
            justifyContent='center'
            fontSize={16}
          >
            {icon}
          </Box>
          <Typography fontWeight={500} fontSize={15} noWrap>
            {title}
          </Typography>
        </Box>
        {badge && (
          <Chip
            label={badge}
            size='small'
            sx={{
              bgcolor: '#F2F2F2',
              color: mutedColor,
              fontSize: 11,
              fontWeight: 500,
              height: 22,
              borderRadius: 4
            }}
          />
        )}
      </Box>
      <Divider sx={{ borderColor: '#ECECEC', mx: 2 }} />

      <Box px={2} pt={1.5} pb={1.5}>
        <Box display='flex' alignItems='center' justifyContent='space-between' gap={1}>
          <Typography fontSize={13} color={mutedColor}>
            {label}
          </Typography>
          <Typography fontSize={15} fontWeight={600} noWrap>
            {format(safeUsed)} / {format(safeLimit)}
          </Typography>
        </Box>

        <Box display='flex' height={8} borderRadius={5} bgcolor='#ECECEC' overflow='hidden' mt={1}>
          {primaryWidth > 0 && <Box width={`${primaryWidth}%`} bgcolor={primaryBarColor} />}
          {overWidth > 0 && <Box width={`${overWidth}%`} bgcolor={theme.palette.error.main} />}
        </Box>

        <Box display='flex' alignItems='center' justifyContent='space-between' gap={1} mt={0.75}>
          <Typography fontSize={11.5} color={mutedColor} noWrap>
            {withUnit(safeLimit)} included
            {statusText && (
              <>
                {' · '}
                <Box component='span' color={statusColor}>
                  {statusText}
                </Box>
              </>
            )}
          </Typography>
          {hasLimit && (
            <Typography fontSize={11.5} color={statusColor} noWrap>
              {percentage}% used
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
